"use client";

import { useState } from "react";
import { ArrowUpRight, Check, Copy, Download, FileKey, Mail } from "lucide-react";
import { useLanguage } from "@/context/language-context";
import { BlurReveal } from "../blur-reveal";

export default function Contact() {
  const { content, rawContent } = useLanguage();
  const [copiedPgp, setCopiedPgp] = useState(false);

  const copyPgp = () => {
    void navigator.clipboard.writeText(rawContent.contact.pgp_key);
    setCopiedPgp(true);
    setTimeout(() => setCopiedPgp(false), 2000);
  };

  return (
    <section className="relative overflow-hidden border-t border-border/50 bg-background pt-24 md:pt-32 xl:pt-48">
      <div className="relative z-10 container mx-auto px-container">
        <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
          <div className="mb-16 flex flex-col gap-4 lg:mb-32">
            <BlurReveal>
              <h2 className="title">{content.contact.title}</h2>
            </BlurReveal>
            <BlurReveal>
              <p className="mt-3 max-w-xl text-lg font-medium tracking-tight text-muted-foreground italic">
                {content.contact.description}
              </p>
            </BlurReveal>
          </div>
        </div>

        <div className="mx-auto mb-12 flex w-full max-w-5xl flex-col border-t border-border/50 sm:mb-24 xl:mb-40">
          <BlurReveal>
            <a
              href={content.contact.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="focus-ring group flex flex-col justify-between rounded-sm border-b border-border/50 py-10 transition-all duration-700 hover:px-8 md:flex-row md:items-center md:py-14"
            >
              <span className="mb-4 font-mono text-sm tracking-widest text-muted-foreground uppercase transition-colors duration-500 group-hover:text-foreground md:mb-0">
                {content.contact.linkedin_label}
              </span>
              <div className="flex items-center gap-8">
                <span className="origin-left text-2xl font-semibold tracking-tight text-foreground transition-all duration-500 group-hover:scale-[1.02] group-hover:text-primary md:origin-right lg:text-3xl">
                  {content.contact.linkedin_display}
                </span>
                <div className="flex hidden h-10 w-10 shrink-0 -translate-x-8 items-center justify-center rounded-full border border-border/50 bg-background opacity-0 transition-all duration-700 group-hover:translate-x-0 group-hover:border-foreground group-hover:bg-foreground group-hover:opacity-100 md:flex">
                  <ArrowUpRight className="h-6 w-6 text-foreground transition-colors duration-500 group-hover:text-background" />
                </div>
              </div>
            </a>
          </BlurReveal>

          <BlurReveal>
            <a
              href={`mailto:${content.contact.email}`}
              className="focus-ring group flex flex-col justify-between rounded-sm border-b border-border/50 py-10 transition-all duration-700 hover:px-8 md:flex-row md:items-center md:py-14"
            >
              <span className="mb-4 font-mono text-sm tracking-widest text-muted-foreground uppercase transition-colors duration-500 group-hover:text-foreground md:mb-0">
                {content.contact.send_email}
              </span>
              <div className="flex items-center gap-8">
                <span className="origin-left text-2xl font-semibold tracking-tight text-foreground transition-all duration-500 group-hover:scale-[1.02] group-hover:text-primary md:origin-right lg:text-3xl">
                  {content.contact.email}
                </span>
                <div className="flex hidden h-10 w-10 shrink-0 -translate-x-8 items-center justify-center rounded-full border border-border/50 bg-background opacity-0 transition-all duration-700 group-hover:translate-x-0 group-hover:border-foreground group-hover:bg-foreground group-hover:opacity-100 md:flex">
                  <Mail className="h-6 w-6 text-foreground transition-colors duration-500 group-hover:text-background" />
                </div>
              </div>
            </a>
          </BlurReveal>

          {content.contact.resume_url && (
            <BlurReveal>
              <a
                href={content.contact.resume_url}
                download
                className="focus-ring group flex flex-col justify-between rounded-sm border-b border-border/50 py-10 transition-all duration-700 hover:px-8 md:flex-row md:items-center md:py-14"
              >
                <span className="mb-4 font-mono text-sm tracking-widest text-muted-foreground uppercase transition-colors duration-500 group-hover:text-foreground md:mb-0">
                  {content.contact.download_resume}
                </span>
                <div className="flex items-center gap-8">
                  <span className="origin-left text-2xl font-semibold tracking-tight text-foreground transition-all duration-500 group-hover:scale-[1.02] group-hover:text-primary md:origin-right lg:text-3xl">
                    Mikkel_Scheike_CV.pdf
                  </span>
                  <div className="hidden h-10 w-10 shrink-0 -translate-x-8 items-center justify-center rounded-full border border-border/50 bg-background opacity-0 transition-all duration-700 group-hover:translate-x-0 group-hover:border-foreground group-hover:bg-foreground group-hover:opacity-100 md:flex">
                    <Download className="h-6 w-6 text-foreground transition-colors duration-500 group-hover:text-background" />
                  </div>
                </div>
              </a>
            </BlurReveal>
          )}

          {content.contact.phone && (
            <BlurReveal>
              <a
                href={`tel:${String(content.contact.phone).replace(/\s+/g, "")}`}
                className="focus-ring group flex flex-col justify-between rounded-sm border-b border-border/50 py-10 transition-all duration-700 hover:px-8 md:flex-row md:items-center md:py-14"
              >
                <span className="mb-4 font-mono text-sm tracking-widest text-muted-foreground uppercase transition-colors duration-500 group-hover:text-foreground md:mb-0">
                  {content.contact.direct_line}
                </span>
                <div className="flex items-center gap-8">
                  <span className="origin-left text-2xl font-semibold tracking-tight text-foreground transition-all duration-500 group-hover:scale-[1.02] group-hover:text-primary md:origin-right lg:text-3xl">
                    {content.contact.phone}
                  </span>
                  <div className="hidden h-10 w-10 shrink-0 -translate-x-8 items-center justify-center rounded-full border border-border/50 bg-background opacity-0 transition-all duration-700 group-hover:translate-x-0 group-hover:border-foreground group-hover:bg-foreground group-hover:opacity-100 md:flex">
                    <ArrowUpRight className="h-6 w-6 text-foreground transition-colors duration-500 group-hover:text-background" />
                  </div>
                </div>
              </a>
            </BlurReveal>
          )}

          <BlurReveal>
            <div className="flex flex-col items-center gap-6 border-b border-border/50 py-10 md:py-14">
              <span className="flex items-center gap-2 font-mono text-sm tracking-widest text-muted-foreground uppercase">
                <FileKey className="h-4 w-4" />
                {content.contact.pgp_label}
              </span>
              <pre className="mx-auto w-full max-w-2xl rounded-xl border border-border/50 bg-secondary/5 p-3 text-center font-mono text-[clamp(0.5rem,2.6vw,0.6875rem)] leading-[1.45] break-all whitespace-pre-wrap text-muted-foreground select-all sm:p-4">
                {rawContent.contact.pgp_key}
              </pre>
              <button
                onClick={copyPgp}
                className="group flex w-fit items-center gap-2 rounded-full border border-border/50 bg-secondary/20 px-4 py-2 font-mono text-sm tracking-widest uppercase transition-all duration-500 hover:border-foreground/30 hover:bg-foreground hover:text-background"
              >
                {copiedPgp ? (
                  <Check className="h-4 w-4 text-green-600 group-hover:text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copiedPgp ? content.contact.copied : content.contact.copy_pgp}
              </button>
            </div>
          </BlurReveal>
        </div>

        <div className="flex w-full flex-col items-center justify-between gap-8 border-border/50 pb-12 md:flex-row xl:border-t xl:py-12">
          <div className="flex items-center gap-4 font-mono text-sm tracking-widest text-muted-foreground uppercase max-xl:hidden">
            <span>© {new Date().getFullYear()}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-primary/50" />
            <span>
              {content.contact.copyright_name} {content.contact.all_rights_reserved}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {content.social.items.map((link: { label: string; href: string }) => (
              <BlurReveal key={link.label}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex h-14 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-background px-8 text-foreground shadow-sm transition-all duration-500 hover:border-foreground/30 hover:bg-foreground hover:text-background"
                >
                  <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-13 justify-center group-hover:translate-x-full group-hover:duration-1000">
                    <div className="relative h-full w-6 bg-background/20 dark:bg-background/20" />
                  </div>
                  <span className="relative z-10 flex items-center gap-3 text-sm font-medium tracking-widest uppercase">
                    {link.label}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </a>
              </BlurReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
